import { useQuery } from '@tanstack/react-query';
import type {
  ByChatHistory,
  CcyHistory,
  ChatBalancesHistory,
  ChatHistoryDetail,
  DailyPositionHistory,
  DayReconciliation,
  HistoryRange,
} from '@wave/shared';
import { api } from '../../api/client';
import { queryKeys } from '../../api/hooks';

// История пишется раз в 15 минут — refetch чаще бессмысленен.
const REFETCH_MS = 5 * 60_000;

export function useBalanceHistory(range: HistoryRange) {
  return useQuery({
    queryKey: queryKeys.chatBalancesHistory(range),
    queryFn: () => api.get<ChatBalancesHistory>('/chat-balances/history', { range }),
    refetchInterval: REFETCH_MS,
    staleTime: 60_000,
  });
}

export function useBalanceHistoryByCcy(range: HistoryRange) {
  return useQuery({
    queryKey: queryKeys.chatBalancesHistoryByCcy(range),
    queryFn: () => api.get<CcyHistory>('/chat-balances/history/by-ccy', { range }),
    refetchInterval: REFETCH_MS,
    staleTime: 60_000,
  });
}

export function useBalanceHistoryByChat(range: HistoryRange) {
  return useQuery({
    queryKey: queryKeys.chatBalancesHistoryByChat(range),
    queryFn: () => api.get<ByChatHistory>('/chat-balances/history/by-chat', { range }),
    refetchInterval: REFETCH_MS,
    staleTime: 60_000,
  });
}

export function useChatHistoryDetail(chatId: string | null, range: HistoryRange) {
  return useQuery({
    queryKey: chatId
      ? queryKeys.chatBalancesChatDetail(chatId, range)
      : ['chat-balances', 'history', 'chat', '__none__'],
    queryFn: () => api.get<ChatHistoryDetail>(`/chat-balances/history/chat/${chatId}`, { range }),
    enabled: !!chatId,
    staleTime: 60_000,
  });
}

export function useDailyPosition(days: number) {
  return useQuery({
    queryKey: queryKeys.chatBalancesDaily(days),
    queryFn: () => api.get<DailyPositionHistory>('/chat-balances/history/daily', { days }),
    refetchInterval: REFETCH_MS,
    staleTime: 60_000,
  });
}

/** date = null — текущие сутки (сервер берёт «сегодня» в бизнес-поясе). */
export function useDayReconciliation(date: string | null) {
  return useQuery({
    queryKey: queryKeys.chatBalancesDayRecon(date),
    queryFn: () =>
      api.get<DayReconciliation>('/chat-balances/history/day-reconciliation', date ? { date } : {}),
    refetchInterval: date ? false : REFETCH_MS,
    staleTime: 60_000,
  });
}
